// Sound and video: WebRTC to the server's gateway (src/media/rtc.xq), which carries them to and from
// the voice room of a voice channel of the meeting. The client sends its microphone and camera and takes
// `SLOTS` pairs of tracks — a page of tiles, each the sound and video of one other member —,
// `VOICE_SLOTS` tracks of sound alone, of members who speak and are not shown, and `SCREEN_SLOTS` tracks
// of video, each a screen another member shows, as the server's `slots` message says; the browser keeps
// each pair's picture with its sound. The page tells the server which members it shows and how large
// (`show`): they take the pairs, and a browser that can (Chromium) sends its camera in layers — VP9's SVC,
// three pictures from 320 × 180 to 1280 × 720 — so that a small tile takes the small picture. A member
// whose video the way to the browser does not take is paused (`slots`): its tile shows its avatar. The
// user's own screen goes on a connection of its own (`screen`), which the server's gateway of the screen
// answers. `?fake=1` (tests): a tone for the microphone, drawings for the camera and the screen, and the
// others' sound is not played.
import { noBoards } from "./boards.ts";
import { send } from "./net.ts";
import type { Server } from "./protocol.ts";
import { changed, nameOf, show, state, type Call, type Meter, type Share, type Slot, type VoiceSlot } from "./store.ts";

/** Pairs of tracks the client takes: as many tiles as a page shows — the server gives them to the
 * members the page shows. */
export const SLOTS = 25;
/** Tracks of sound alone the client takes: as many members not shown who speak at once it hears. */
const VOICE_SLOTS = 3;
/** Tracks of screens the client takes: as many screens shown at once it shows. */
const SCREEN_SLOTS = 2;
/** The header extensions of the tracks the client takes that the server sends — the level of the sound,
 * the transport-wide number — and MID, which the browser does not let go (`trim`). */
const AUDIO_LEVEL = "urn:ietf:params:rtp-hdrext:ssrc-audio-level";
const TRANSPORT_CC = "http://www.ietf.org/id/draft-holmer-rmcat-transport-wide-cc-extensions-01";
const MID = "urn:ietf:params:rtp-hdrext:sdes:mid";
/** Whether the browser sends its camera in layers: VP9's SVC, three pictures and three rates of each
 * (`L3T3_KEY`) — Chromium; the others send one picture of VP8. A browser that cannot refuses the mode
 * (or leaves it out of the encoding it keeps); none tells it otherwise. */
const SVC = (() => {
  try {
    const vp9 = (RTCRtpSender.getCapabilities?.("video")?.codecs ?? []).some((c) => c.mimeType.toLowerCase() === "video/vp9");
    const pc = new RTCPeerConnection();
    const encoding = { scalabilityMode: "L3T3_KEY" } as RTCRtpEncodingParameters;
    const t = pc.addTransceiver("video", { direction: "sendonly", sendEncodings: [encoding] });
    const kept = t.sender.getParameters().encodings[0] as { scalabilityMode?: string } | undefined;
    pc.close();
    return vp9 && kept?.scalabilityMode === "L3T3_KEY";
  } catch {
    return false;
  }
})();
/** The camera: the size and rate asked for, and the most it sends, bits a second — all its layers. */
const CAMERA = SVC ? { width: 1280, height: 720, frameRate: 30 } : { width: 640, height: 360, frameRate: 30 };
const CAMERA_BPS = SVC ? 1700000 : 800000;
/** The screen: the rate asked for — text matters, not motion — and the most it sends, bits a second;
 * a test's drawing of this size. */
const SCREEN = { width: 1280, height: 720, frameRate: 15 };
const SCREEN_BPS = 2500000;
/** Who speaks: looked at every `SPEAKING_EVERY` ms, a level over `SPEAKING` (0 to 1). Who speaks large
 * keeps it while speaking and `SPEAKER_HOLD` ms after. */
const SPEAKING_EVERY = 200;
const SPEAKING = 0.02;
const SPEAKER_HOLD = 1500;
/** A test without devices (`?fake=1`). */
export const FAKE = new URLSearchParams(location.search).has("fake");
/** A test through a relay that narrows the way to the browser (`?relay=<port>`, src/bots/relay.xq): the
 * call goes to the relay's port on the server's host. */
const RELAY = Number(new URLSearchParams(location.search).get("relay") ?? 0);
/** Noise suppression of the microphone (RNNoise, worklet/denoise.ts): on, but in tests (whose tone it
 * would take for noise) unless `?denoise=1`; `?denoise=0` turns it off. */
const DENOISE = (() => {
  const d = new URLSearchParams(location.search).get("denoise");
  return d === null ? !FAKE : d !== "0";
})();
/** Why there is no microphone or camera on a page of plain HTTP (but on localhost). */
const INSECURE = "микрофон и камера — только по HTTPS: запустите сервер с --cert и --key (scripts/certs.py)";

/** The user joins the sound of voice channel `channel`: the microphone (if there is one), the camera if
 * it is on, and `SLOTS` pairs of tracks to take, offered to the server. A call in another channel ends
 * first. */
export async function joinCall(channel: number): Promise<void> {
  const meeting = state.meeting;
  if (meeting === null || state.call?.channel === channel) return;
  if (state.call !== null) hangup(true);
  state.wantCall = channel;
  // The call's boards come from the server, which sends those it has to one who comes.
  noBoards();
  const call: Call = {
    pc: new RTCPeerConnection({ bundlePolicy: "max-bundle", rtcpMuxPolicy: "require" }),
    community: meeting.number,
    channel,
    session: -1,
    mic: null,
    micStop: null,
    denoised: false,
    cam: null,
    camStop: null,
    micTx: null,
    camTx: null,
    slots: [],
    voices: [],
    screens: [],
    paused: [],
    share: null,
    level: null,
    timer: 0,
    self: null,
    locked: false,
  };
  state.call = call;
  state.page = 0;
  show("подключаюсь к голосовому каналу…");
  try {
    [call.mic, call.micStop, call.denoised] = await micTrack();
  } catch (err) {
    show(window.isSecureContext ? `без микрофона: ${(err as Error).message}` : INSECURE, true);
  }
  if (state.call !== call) {
    call.mic?.stop();
    return call.micStop?.();
  }
  const pc = call.pc;
  call.micTx = pc.addTransceiver(call.mic ?? "audio", { direction: "sendonly" });
  const encoding: RTCRtpEncodingParameters & { scalabilityMode?: string } = {
    maxBitrate: CAMERA_BPS,
    maxFramerate: CAMERA.frameRate,
  };
  if (SVC) encoding.scalabilityMode = "L3T3_KEY";
  call.camTx = pc.addTransceiver("video", { direction: "sendonly", sendEncodings: [encoding] });
  for (let k = 0; k < SLOTS; k++) {
    call.slots.push({
      audio: pc.addTransceiver("audio", { direction: "recvonly" }),
      video: pc.addTransceiver("video", { direction: "recvonly" }),
      session: -1,
      audioEl: null,
      videoEl: null,
    });
  }
  // After the pairs: the tracks of screens, then those of sound alone. The server tells them by their
  // place — a pair is a track of sound right before one of video —, so none of sound alone comes before
  // one of video.
  for (let k = 0; k < SCREEN_SLOTS; k++) {
    call.screens.push({ video: pc.addTransceiver("video", { direction: "recvonly" }), session: -1, videoEl: null });
  }
  for (let k = 0; k < VOICE_SLOTS; k++) {
    call.voices.push({ audio: pc.addTransceiver("audio", { direction: "recvonly" }), session: -1, audioEl: null });
  }
  // The server's codecs: the offer is shorter, and nothing else is picked. The camera sends VP9 in layers
  // or VP8; the slots and screens take both.
  const all = pc.getTransceivers();
  prefer(all.filter((t) => t.receiver.track.kind === "audio"), "audio", ["audio/opus"]);
  prefer([call.camTx], "video", SVC ? ["video/vp9", "video/vp8"] : ["video/vp8"]);
  prefer([...call.slots.map((s) => s.video), ...call.screens.map((s) => s.video)], "video", ["video/vp9", "video/vp8"]);
  trim([...call.slots.map((s) => s.audio), ...call.voices.map((s) => s.audio)], [AUDIO_LEVEL, TRANSPORT_CC, MID]);
  trim([...call.slots.map((s) => s.video), ...call.screens.map((s) => s.video)], [TRANSPORT_CC, MID]);
  pc.ontrack = (e) => tracked(call, e);
  pc.onconnectionstatechange = () => {
    if (state.call !== call) return;
    const s = pc.connectionState;
    if (s === "connected") {
      const said = call.denoised ? "вы в голосовом канале, шумоподавление включено" : "вы в голосовом канале";
      show(said);
      // The bar below says it from now on.
      setTimeout(() => state.status === said && show(""), 4000);
    } else if (s === "failed") show("связь с голосовым каналом прервалась: сервер недоступен по UDP?", true);
  };
  if (call.mic !== null) {
    call.mic.enabled = !state.micOff;
    call.level = meter(call.mic);
  }
  try {
    await pc.setLocalDescription(await pc.createOffer());
  } catch (err) {
    show(`не удалось подключиться к голосовому каналу: ${(err as Error).message}`, true);
    return hangup(true);
  }
  if (state.call !== call) return;
  send({ type: "rtc", community: call.community, channel: call.channel, sdp: pc.localDescription!.sdp });
  call.timer = window.setInterval(() => speakers(call), SPEAKING_EVERY);
  if (state.camOn) await camera(true);
  changed();
}

/** Only codecs `mimes`, in this order, for transceivers `ts` of `kind`, where the browser lets the page
 * say; of VP9 only profile 0, which the server takes. */
function prefer(ts: RTCRtpTransceiver[], kind: "audio" | "video", mimes: string[]): void {
  const caps = RTCRtpReceiver.getCapabilities?.(kind)?.codecs ?? [];
  const zero = (c: RTCRtpCodec) => c.mimeType.toLowerCase() !== "video/vp9" || (c.sdpFmtpLine ?? "profile-id=0").includes("profile-id=0");
  const codecs = mimes.flatMap((mime) => caps.filter((c) => c.mimeType.toLowerCase() === mime && zero(c)));
  if (codecs.length === 0) return;
  for (const t of ts) {
    if (!t.setCodecPreferences) continue;
    try {
      t.setCodecPreferences(codecs);
    } catch {
      // The browser picks; the server takes only its codecs anyway.
    }
  }
}

/** A header extension a transceiver offers (`getHeaderExtensionsToNegotiate`), and what the page sees of
 * those of a browser that lets it say which. */
type HeaderExtension = { uri: string; direction: string };
type Negotiating = {
  getHeaderExtensionsToNegotiate?(): HeaderExtension[];
  setHeaderExtensionsToNegotiate?(extensions: HeaderExtension[]): void;
};

/** Of the header extensions transceivers `ts` offer, only `uris`, where the browser lets the page say:
 * Chromium's offer of all the call's tracks is some 65 KB, and some 42 KB so. */
function trim(ts: RTCRtpTransceiver[], uris: string[]): void {
  for (const t of ts) {
    const n = t as unknown as Negotiating;
    if (!n.getHeaderExtensionsToNegotiate || !n.setHeaderExtensionsToNegotiate) return;
    try {
      n.setHeaderExtensionsToNegotiate(
        n.getHeaderExtensionsToNegotiate().map((e) => (uris.includes(e.uri) ? e : { ...e, direction: "stopped" })),
      );
    } catch {
      // The browser keeps them: the offer is longer, nothing else.
    }
  }
}

/** The server's answer: the call connects; its gateway hears again what the page shows. */
export async function answered(m: Server<"rtc">): Promise<void> {
  const call = state.call;
  if (call === null || m.channel !== call.channel) return;
  call.session = m.session;
  told = "";
  changed();
  // Through a relay, its port instead of the server's in the candidate (host port typ host).
  const sdp = RELAY > 0 ? m.sdp.replace(/^(a=candidate:\S+ \d+ \S+ \d+ \S+ )\d+/gm, `$1${RELAY}`) : m.sdp;
  try {
    await call.pc.setRemoteDescription({ type: "answer", sdp });
  } catch (err) {
    show(`не удалось подключиться к голосовому каналу: ${(err as Error).message}`, true);
    hangup(true);
  }
}

/** A track of a slot came: its sound plays, its picture waits for its tile — a screen's for the stage. */
function tracked(call: Call, e: RTCTrackEvent): void {
  const screen = call.screens.find((s) => s.video === e.transceiver);
  if (screen !== undefined) {
    screen.videoEl = video(new MediaStream([e.track]), false);
    return changed();
  }
  const stream = new MediaStream([e.track]);
  const heard = hearing(call).find((s) => s.audio === e.transceiver);
  if (heard !== undefined) {
    const el = new Audio();
    el.autoplay = true;
    el.muted = FAKE || state.deafened;
    el.srcObject = stream;
    el.play().catch(unlockAudio);
    heard.audioEl = el;
    return;
  }
  const slot = call.slots.find((s) => s.video === e.transceiver);
  if (slot === undefined) return;
  slot.videoEl = video(stream, false);
  changed();
}

/** The call's tracks of sound: the pairs', and those of sound alone. */
function hearing(call: Call): (Slot | VoiceSlot)[] {
  return [...call.slots, ...call.voices];
}

/** A video element of `stream`, playing; mirrored if `mirror` (the user's own). */
function video(stream: MediaStream, mirror: boolean): HTMLVideoElement {
  const el = document.createElement("video");
  el.autoplay = true;
  el.playsInline = true;
  el.muted = true;
  if (mirror) el.className = "mirror";
  el.srcObject = stream;
  return el;
}

/** The browser would not play sound before the user touched the page (a phone without the microphone
 * open, say): the first touch plays it. */
function unlockAudio(): void {
  if (state.call === null || state.call.locked) return;
  state.call.locked = true;
  show("коснитесь страницы, чтобы слышать собеседников", true);
  document.addEventListener(
    "click",
    () => {
      const call = state.call;
      if (call === null) return;
      call.locked = false;
      for (const s of hearing(call)) s.audioEl?.play().catch(() => {});
      show("");
    },
    { once: true },
  );
}

/** Whose sound and video the slots carry now — the pairs first, then the tracks of sound alone, then the
 * screens' — and whose video does not go. */
export function slotted(m: Server<"slots">): void {
  const call = state.call;
  if (call === null || m.channel !== call.channel) return;
  const [pairs, voices] = [call.slots.length, call.voices.length];
  m.sessions.forEach((s, i) => {
    if (i < pairs) call.slots[i].session = s;
    else if (i < pairs + voices) call.voices[i - pairs].session = s;
    else if (i < pairs + voices + call.screens.length) call.screens[i - pairs - voices].session = s;
  });
  call.paused = m.paused;
  changed();
}

/** Who is in the call's voice room, whose camera is on, and which members are screens. */
export function roomed(m: Server<"room">): void {
  if (state.call === null || m.channel !== state.call.channel) return;
  state.room = { sessions: m.sessions, users: m.users, cameras: m.cameras, screens: m.screens };
  changed();
}

/** The user of voice session `s` (-1 if not known). */
function userOf(s: number): number {
  const i = state.room.sessions.indexOf(s);
  return i < 0 ? -1 : state.room.users[i];
}

/** The voice session of user `u`'s sound and camera — not of its screen (-1 if not known). */
function sessionOf(u: number): number {
  const i = state.room.users.findIndex((x, k) => x === u && !state.room.screens.includes(state.room.sessions[k]));
  return i < 0 ? -1 : state.room.sessions[i];
}

/** The picture a tile `h` pixels of the page high needs, by the screen's pixels: 0 (320 × 180), 1
 * (640 × 360) or 2 (1280 × 720). */
export function layerFor(h: number): number {
  const px = h * devicePixelRatio;
  return px <= 240 ? 0 : px <= 480 ? 1 : 2;
}

/** What the server heard the page shows, and the timer of what it hears next. */
let told = "";
let telling = 0;

/** The page shows the cameras of `users`, the most important first, in tiles `heights` pixels high: the
 * server hears the picture each needs (`show`) — when that changed, after it stayed 200 ms. */
export function showTiles(users: number[], heights: number[]): void {
  const call = state.call;
  if (call === null || call.session < 0) return;
  const sessions: number[] = [];
  const layers: number[] = [];
  users.forEach((u, i) => {
    const s = sessionOf(u);
    if (s < 0 || s === call.session) return;
    sessions.push(s);
    layers.push(layerFor(heights[i]));
  });
  const text = `${sessions}/${layers}`;
  if (text === told) return;
  told = text;
  clearTimeout(telling);
  telling = window.setTimeout(() => {
    if (state.call === call) send({ type: "show", channel: call.channel, sessions, layers });
  }, 200);
}

/** The user leaves the sound (`tell`: the server hears it; a lost connection took it already). */
export function hangup(tell: boolean): void {
  const call = state.call;
  if (call === null) return;
  state.call = null;
  state.room = { sessions: [], users: [], cameras: [], screens: [] };
  noBoards();
  if (tell) {
    state.wantCall = -1;
    send({ type: "hangup", channel: call.channel });
  }
  clearInterval(call.timer);
  // Leaving the room ends the screen there too.
  unshare(call, false);
  call.pc.close();
  call.mic?.stop();
  call.micStop?.();
  call.cam?.stop();
  call.camStop?.();
  call.level?.close();
  for (const s of hearing(call)) if (s.audioEl) s.audioEl.srcObject = null;
  state.speaking = new Set();
  changed();
}

/** The microphone on or off: off, its track sends silence, which the server does not pass on. */
export function microphone(on: boolean): void {
  state.micOff = !on;
  const mic = state.call?.mic;
  if (mic) mic.enabled = on;
  changed();
}

/** The others' sound off (the user hears no one, in this call and the next) or on again. */
export function deafen(on: boolean): void {
  state.deafened = on;
  const call = state.call;
  if (call !== null) for (const s of hearing(call)) if (s.audioEl) s.audioEl.muted = FAKE || on;
  changed();
}

/** The camera on or off. */
export async function camera(on: boolean): Promise<void> {
  state.camOn = on;
  const call = state.call;
  if (call === null) return changed();
  if (on && call.cam === null) {
    try {
      const [track, stop] = await camTrack();
      if (state.call !== call || !state.camOn) {
        track.stop();
        stop?.();
        return;
      }
      call.cam = track;
      call.camStop = stop;
      await call.camTx!.sender.replaceTrack(track);
    } catch (err) {
      state.camOn = false;
      show(window.isSecureContext ? `без камеры: ${(err as Error).message}` : INSECURE, true);
      return changed();
    }
  } else if (!on && call.cam !== null) {
    await call.camTx!.sender.replaceTrack(null);
    call.cam.stop();
    call.camStop?.();
    call.cam = null;
    call.camStop = null;
    call.self = null;
  }
  send({ type: "camera", channel: call.channel, on: state.camOn });
  changed();
}

/** Whether the browser can show a screen: a computer's can, a phone's cannot. */
export function canShare(): boolean {
  return FAKE || typeof navigator.mediaDevices?.getDisplayMedia === "function";
}

/** The user's screen shown, or no more: it goes on a connection of its own, which the server answers
 * (`shared`); the browser's own bar that stops showing it stops it here too. */
export async function screen(on: boolean): Promise<void> {
  const call = state.call;
  if (call === null) return;
  if (!on) return unshare(call, true);
  if (call.share !== null) return;
  let source: [MediaStreamTrack, (() => void) | null];
  try {
    source = await screenTrack();
  } catch (err) {
    // The user chose no screen: nothing to say.
    if ((err as Error).name !== "NotAllowedError") show(`экран не показан: ${(err as Error).message}`, true);
    return;
  }
  const [track, stop] = source;
  if (state.call !== call || call.share !== null) {
    track.stop();
    return stop?.();
  }
  const pc = new RTCPeerConnection({ bundlePolicy: "max-bundle", rtcpMuxPolicy: "require" });
  const share: Share = { pc, track, stop, session: -1, self: null };
  call.share = share;
  // Text sharp rather than motion smooth.
  track.contentHint = "detail";
  track.onended = () => {
    if (call.share === share) void screen(false);
  };
  const tx = pc.addTransceiver(track, {
    direction: "sendonly",
    sendEncodings: [{ maxBitrate: SCREEN_BPS, maxFramerate: SCREEN.frameRate }],
  });
  prefer([tx], "video", ["video/vp8"]);
  pc.onconnectionstatechange = () => {
    if (call.share === share && pc.connectionState === "failed") {
      show("показ экрана прервался: сервер недоступен по UDP?", true);
      unshare(call, true);
    }
  };
  changed();
  try {
    await pc.setLocalDescription(await pc.createOffer());
  } catch (err) {
    show(`экран не показан: ${(err as Error).message}`, true);
    return unshare(call, false);
  }
  if (call.share === share) send({ type: "share", channel: call.channel, sdp: pc.localDescription!.sdp });
}

/** The server's answer to the screen's offer: the screen goes. */
export async function shared(m: Server<"share">): Promise<void> {
  const call = state.call;
  const share = call?.share;
  if (!call || !share || m.channel !== call.channel) return;
  share.session = m.session;
  try {
    await share.pc.setRemoteDescription({ type: "answer", sdp: m.sdp });
  } catch (err) {
    show(`экран не показан: ${(err as Error).message}`, true);
    unshare(call, true);
  }
}

/** The server would not take the screen: it is shown no more. */
export function unshared(): void {
  if (state.call !== null) unshare(state.call, false);
}

/** The user's screen shown no more: its connection closed, its track stopped; `tell`: the server
 * hears it (leaving the room ends the screen without a word). */
function unshare(call: Call, tell: boolean): void {
  const share = call.share;
  if (share === null) return;
  call.share = null;
  if (tell) send({ type: "share", channel: call.channel, sdp: "" });
  share.track.onended = null;
  share.pc.close();
  share.track.stop();
  share.stop?.();
  changed();
}

/** The screen's track — which screen, window or tab, the browser asks the user — and what stops it
 * besides the track; a drawing in a test. A screen larger than 1920 × 1080 comes smaller: a keyframe of
 * a 4K screen would be more pieces than a member may send at once (`limits.xq`). */
async function screenTrack(): Promise<[MediaStreamTrack, (() => void) | null]> {
  if (FAKE) return fakeScreen();
  const s = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: { ideal: SCREEN.frameRate, max: 30 }, width: { max: 1920 }, height: { max: 1080 } },
    audio: false,
  });
  return [s.getVideoTracks()[0], null];
}

/** The microphone's track — with echo cancellation, as a call's, and RNNoise's suppression of noise where
 * the server has it — what stops it besides the track, and whether it is suppressed; a tone in a test. */
async function micTrack(): Promise<[MediaStreamTrack, (() => void) | null, boolean]> {
  // The page's audio starts while the click that joined is on: Safari starts it only then.
  if (DENOISE) audioContext();
  const raw = FAKE
    ? await fakeMic()
    : (
        await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        })
      ).getAudioTracks()[0];
  const d = DENOISE ? await denoised(raw) : null;
  return d === null ? [raw, null, false] : [d[0], d[1], true];
}

/** RNNoise compiled, once (null: the server has none, or the browser cannot). */
let rnnoise: WebAssembly.Module | null | undefined;

async function rnnoiseModule(): Promise<WebAssembly.Module | null> {
  if (rnnoise !== undefined) return rnnoise;
  try {
    const r = await fetch("/rnnoise.wasm");
    if (!r.ok) throw new Error(`rnnoise.wasm: ${r.status}`);
    rnnoise = await WebAssembly.compile(await r.arrayBuffer());
    await audioContext().audioWorklet.addModule("/denoise.js");
  } catch (err) {
    console.warn("no noise suppression:", err);
    rnnoise = null;
  }
  return rnnoise;
}

/** Track `raw` through RNNoise (worklet/denoise.ts): the track it comes out as, and what stops the way
 * (and `raw`); null if it cannot be done. */
async function denoised(raw: MediaStreamTrack): Promise<[MediaStreamTrack, () => void] | null> {
  const module = await rnnoiseModule();
  if (module === null) return null;
  // RNNoise takes 48 kHz only; and the page's audio must run, or the way would send silence: else the
  // microphone goes as it is.
  const ctx = audioContext();
  if (ctx.sampleRate !== 48000 || !(await running(ctx))) {
    console.warn("no noise suppression: the page's audio", ctx.sampleRate, ctx.state);
    return null;
  }
  try {
    const source = ctx.createMediaStreamSource(new MediaStream([raw]));
    const node = new AudioWorkletNode(ctx, "rnnoise", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 1,
      channelCountMode: "explicit",
      processorOptions: { module },
    });
    const dest = ctx.createMediaStreamDestination();
    source.connect(node).connect(dest);
    const stop = () => {
      source.disconnect();
      node.disconnect();
      node.port.close();
      raw.stop();
    };
    return [dest.stream.getAudioTracks()[0], stop];
  } catch (err) {
    console.warn("no noise suppression:", err);
    return null;
  }
}

/** The camera's track, and what stops it besides the track; a drawing in a test. */
async function camTrack(): Promise<[MediaStreamTrack, (() => void) | null]> {
  if (FAKE) return fakeCamera();
  const s = await navigator.mediaDevices.getUserMedia({
    video: {
      width: { ideal: CAMERA.width },
      height: { ideal: CAMERA.height },
      frameRate: { ideal: CAMERA.frameRate },
    },
  });
  return [s.getVideoTracks()[0], null];
}

let audio: AudioContext | null = null;

/** Whether the page's audio runs, given a second to start. */
async function running(ctx: AudioContext): Promise<boolean> {
  if (ctx.state === "running") return true;
  await Promise.race([ctx.resume(), new Promise((r) => setTimeout(r, 1000))]).catch(() => {});
  // The wait may have changed it, though TypeScript takes it for the one it was.
  return (ctx.state as string) === "running";
}

/** The page's audio, at 48 kHz: RNNoise takes nothing else. */
function audioContext(): AudioContext {
  if (audio === null) {
    try {
      audio = new AudioContext({ sampleRate: 48000 });
    } catch {
      audio = new AudioContext();
    }
  }
  audio.resume().catch(() => {});
  return audio;
}

/** When the test's tone started, by the audio's clock: the drawing flashes while it sounds. */
let toneAt = -1;

/** Whether the test's tone sounds now. */
function toneOn(): boolean {
  return toneAt >= 0 && (audioContext().currentTime - toneAt) % 1 < 0.5;
}

/** A tone of half a second, every second: speech and silence by turns; `?fake=name.wav` plays that
 * sound of the server's, round and round. */
async function fakeMic(): Promise<MediaStreamTrack> {
  const ctx = audioContext();
  const dest = ctx.createMediaStreamDestination();
  const file = new URLSearchParams(location.search).get("fake");
  if (file !== null && file.endsWith(".wav")) {
    const r = await fetch(`/${file}`);
    const sound = ctx.createBufferSource();
    sound.buffer = await ctx.decodeAudioData(await r.arrayBuffer());
    sound.loop = true;
    sound.connect(dest);
    sound.start();
    return dest.stream.getAudioTracks()[0];
  }
  const osc = ctx.createOscillator();
  osc.frequency.value = 440;
  const gain = ctx.createGain();
  gain.gain.value = 0;
  const t = ctx.currentTime + 0.1;
  toneAt = t;
  for (let i = 0; i < 3600; i++) {
    gain.gain.setValueAtTime(0.3, t + i);
    gain.gain.setValueAtTime(0, t + i + 0.5);
  }
  osc.connect(gain).connect(dest);
  osc.start();
  return dest.stream.getAudioTracks()[0];
}

/** A drawing for the camera: the user's name, a counter of frames and a bar that moves; in its lower
 * right corner a white square while the tone sounds, black otherwise — a test sees by them whether
 * pictures come with their sound. */
function fakeCamera(): [MediaStreamTrack, () => void] {
  const canvas = document.createElement("canvas");
  canvas.width = CAMERA.width;
  canvas.height = CAMERA.height;
  const g = canvas.getContext("2d")!;
  let n = 0;
  const draw = () => {
    n++;
    g.fillStyle = `hsl(${(n * 2) % 360} 45% 30%)`;
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.fillStyle = "#fff";
    g.font = "bold 44px system-ui, sans-serif";
    g.fillText(`${nameOf(state.user)} · ${n}`, 32, 80);
    g.fillRect((n * 8) % canvas.width, 160, 48, 160);
    g.fillStyle = toneOn() ? "#fff" : "#000";
    g.fillRect(canvas.width - 96, canvas.height - 96, 96, 96);
  };
  // Drawn and taken as the audio's clock goes, every 5 ms: the timers of a page out of sight run once
  // a second, the audio does not stop. A picture is taken `CAMERA.frameRate` times a second.
  const track = canvas.captureStream(0).getVideoTracks()[0] as CanvasCaptureMediaStreamTrack;
  const ctx = audioContext();
  const clock = ctx.createScriptProcessor(256, 1, 1);
  const silent = ctx.createGain();
  silent.gain.value = 0;
  let last = -Infinity;
  clock.onaudioprocess = () => {
    const now = performance.now();
    if (now - last < 1000 / CAMERA.frameRate - 2) return;
    last = now;
    draw();
    track.requestFrame();
  };
  clock.connect(silent).connect(ctx.destination);
  return [
    track,
    () => {
      clock.onaudioprocess = null;
      clock.disconnect();
    },
  ];
}

/** A drawing for the screen: a slide with the user's name, the time to the tenth of a second and a
 * counter of frames, and a bar that moves — a test sees by them that the screen comes and goes on. Drawn
 * by the audio's clock, as the camera's. */
function fakeScreen(): [MediaStreamTrack, () => void] {
  const canvas = document.createElement("canvas");
  canvas.width = SCREEN.width;
  canvas.height = SCREEN.height;
  const g = canvas.getContext("2d")!;
  let n = 0;
  const draw = () => {
    n++;
    g.fillStyle = "#f4f1ea";
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.fillStyle = "#1d3557";
    g.fillRect(0, 0, canvas.width, 120);
    g.fillStyle = "#fff";
    g.font = "bold 56px system-ui, sans-serif";
    g.fillText(`Экран: ${nameOf(state.user)}`, 48, 82);
    g.fillStyle = "#222";
    g.font = "40px ui-monospace, monospace";
    g.fillText(`время ${(performance.now() / 1000).toFixed(1)} с · кадр ${n}`, 48, 220);
    g.font = "32px system-ui, sans-serif";
    ["Мелкий текст должен читаться:", "строка за строкой, буква за буквой."].forEach((l, i) => g.fillText(l, 48, 320 + 48 * i));
    g.fillStyle = "#e63946";
    g.fillRect((n * 12) % canvas.width, canvas.height - 120, 160, 80);
  };
  const track = canvas.captureStream(0).getVideoTracks()[0] as CanvasCaptureMediaStreamTrack;
  const ctx = audioContext();
  const clock = ctx.createScriptProcessor(256, 1, 1);
  const silent = ctx.createGain();
  silent.gain.value = 0;
  let last = -Infinity;
  clock.onaudioprocess = () => {
    const now = performance.now();
    if (now - last < 1000 / SCREEN.frameRate - 2) return;
    last = now;
    draw();
    track.requestFrame();
  };
  clock.connect(silent).connect(ctx.destination);
  return [
    track,
    () => {
      clock.onaudioprocess = null;
      clock.disconnect();
    },
  ];
}

/** How loud the user's microphone is (0 to 1), for the user's own tile. */
function meter(track: MediaStreamTrack): Meter | null {
  try {
    const ctx = audioContext();
    const source = ctx.createMediaStreamSource(new MediaStream([track]));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const data = new Float32Array(analyser.fftSize);
    return {
      level() {
        analyser.getFloatTimeDomainData(data);
        let sum = 0;
        for (const x of data) sum += x * x;
        return Math.sqrt(sum / data.length);
      },
      close() {
        source.disconnect();
      },
    };
  } catch {
    return null;
  }
}

/** Who speaks now: the slots' sound by its level, the user by the microphone's; and who of the others
 * speaks large — the one there while it speaks and `SPEAKER_HOLD` ms after, then one who speaks now. */
function speakers(call: Call): void {
  if (state.call !== call) return;
  const now = new Set<number>();
  for (const s of hearing(call)) {
    if (s.session < 0) continue;
    const src = s.audio.receiver.getSynchronizationSources?.() ?? [];
    const recent = src.find((x) => performance.timeOrigin + performance.now() - x.timestamp < 500);
    if (recent !== undefined && (recent.audioLevel ?? 0) > SPEAKING) now.add(userOf(s.session));
  }
  if (call.mic?.enabled && call.level && call.level.level() > SPEAKING) now.add(state.user);
  const differs = now.size !== state.speaking.size || [...now].some((u) => !state.speaking.has(u));
  state.speaking = now;
  const t = performance.now();
  const others = [...now].filter((u) => u !== state.user);
  const moves = !others.includes(state.speaker) && others.length > 0 && t - state.speakerAt > SPEAKER_HOLD;
  if (others.includes(state.speaker) || moves) state.speakerAt = t;
  if (moves) state.speaker = others[0];
  if (differs || moves) changed();
}

/** The picture to show in the tile of user `id`: the user's own camera, or a slot's video. */
export function videoOf(id: number): HTMLVideoElement | null {
  const call = state.call;
  if (call === null) return null;
  if (id === state.user) {
    if (!state.camOn || call.cam === null) return null;
    call.self ??= video(new MediaStream([call.cam]), true);
    return call.self;
  }
  for (const s of call.slots) {
    const shows = state.room.cameras.includes(s.session) && !call.paused.includes(s.session);
    if (s.session >= 0 && s.videoEl !== null && userOf(s.session) === id && shows) return s.videoEl;
  }
  return null;
}

/** A screen shown in the voice room: whose, and its picture (null until it comes). */
export type Shown = { user: number; el: HTMLVideoElement | null };

/** The screens shown in the voice room now, the user's own first. */
export function screensShown(): Shown[] {
  const call = state.call;
  if (call === null) return [];
  const share = call.share;
  const own: Shown[] = share === null ? [] : [{ user: state.user, el: (share.self ??= video(new MediaStream([share.track]), false)) }];
  const others = state.room.screens
    .filter((s) => userOf(s) !== state.user)
    .map((s): Shown => ({ user: userOf(s), el: call.screens.find((x) => x.session === s)?.videoEl ?? null }));
  return [...own, ...others];
}

/** For tests: what the call sends and takes, from the browser's statistics. */
export async function callStats(): Promise<unknown> {
  const call = state.call;
  if (call === null) return null;
  const report = await call.pc.getStats();
  const all = [...report.values()] as Record<string, unknown>[];
  const pick = (r: Record<string, unknown>, keys: string[]) =>
    Object.fromEntries(keys.filter((k) => r[k] !== undefined).map((k) => [k, r[k]]));
  const pair = all.find((r) => r.type === "candidate-pair" && r.nominated);
  return {
    state: call.pc.connectionState,
    session: call.session,
    slots: call.slots.map((s) => s.session),
    voices: call.voices.map((s) => s.session),
    screens: call.screens.map((s) => s.session),
    paused: call.paused,
    share: call.share === null ? null : { session: call.share.session, state: call.share.pc.connectionState },
    pair: pair ? pick(pair, ["state", "currentRoundTripTime", "bytesSent", "bytesReceived"]) : null,
    out: all
      .filter((r) => r.type === "outbound-rtp")
      .map((r) =>
        pick(r, ["kind", "ssrc", "packetsSent", "framesEncoded", "frameWidth", "frameHeight", "targetBitrate", "qualityLimitationReason", "nackCount", "pliCount"]),
      ),
    in: all
      .filter((r) => r.type === "inbound-rtp" && (r.packetsReceived as number) > 0)
      .map((r) =>
        pick(r, ["kind", "ssrc", "packetsReceived", "packetsLost", "jitter", "framesDecoded", "framesDropped", "frameWidth", "frameHeight", "freezeCount", "totalSamplesReceived", "concealedSamples", "audioLevel", "jitterBufferDelay", "jitterBufferEmittedCount", "nackCount", "pliCount"]),
      ),
  };
}
